import { Skeleton } from '@art-of-hospitality-solutions-llc/ui-kit/components/skeleton';
import { Loader2 } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { cn } from '@Utils/cn';
import { useEventReports } from '@Hooks/useEventReports';
import EventReportCard from './EventReportCard';
import { useEventReportRunStore } from './eventReportRunStore';

// Stop polling after this long even if no new report shows up, so a failed or
// empty run doesn't leave the "thinking" thought spinning forever.
const RUN_TIMEOUT_MS = 3 * 60 * 1000;
const POLL_INTERVAL_MS = 4000;

/** A node on the thought-stream timeline; pulses for the newest item. */
function Node({ pulse }: { pulse?: boolean }) {
  return (
    <span className="absolute top-4 left-[9px] z-10 flex h-3 w-3 -translate-x-1/2 items-center justify-center">
      {pulse && (
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
      )}
      <span
        className={cn(
          'relative h-2.5 w-2.5 rounded-full ring-2 ring-white',
          pulse ? 'bg-amber-500' : 'bg-zinc-300',
        )}
      />
    </span>
  );
}

/** Transient thought shown while a manually-triggered run is in progress. */
function ThinkingThought() {
  return (
    <div className="agent-thought-in relative pl-7">
      <Node pulse />
      <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
        <div className="flex items-center gap-2 text-sm font-medium text-amber-800">
          <Loader2 className="h-4 w-4 animate-spin" />
          Crunching the numbers…
        </div>
        <p className="mt-1 text-xs text-amber-700/80">
          Tallying ticket sales, revenue, and attendance to write the recap. This
          usually takes a minute or two.
        </p>
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <Skeleton className="h-3 w-40" />
        <Skeleton className="h-3 w-20" />
      </div>
      <Skeleton className="mt-3 h-4 w-full" />
    </div>
  );
}

interface Props {
  soulId: string;
  onOpenReport: (id: string) => void;
}

export default function EventReportFeed({ soulId, onOpenReport }: Props) {
  const pendingRun = useEventReportRunStore((s) => s.pending[soulId]);
  const endRun = useEventReportRunStore((s) => s.end);
  const isPending = !!pendingRun;

  const { data, status } = useEventReports({
    soulId,
    refetchInterval: isPending ? POLL_INTERVAL_MS : false,
  });

  // Newest first by generation time, independent of server/cache ordering.
  const reports = data
    ? [...data].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )
    : data;

  // Snapshot the report IDs known when the run started; once an ID outside that
  // set appears, the new report has landed and we can drop the thinking thought.
  const baselineIds = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (isPending) {
      if (baselineIds.current === null) {
        baselineIds.current = new Set((reports ?? []).map((r) => r.id));
      }
    } else {
      baselineIds.current = null;
    }
  }, [isPending, reports]);

  useEffect(() => {
    if (!isPending) return;
    const baseline = baselineIds.current;
    const hasNew =
      baseline != null && (reports ?? []).some((r) => !baseline.has(r.id));
    const timedOut = Date.now() - (pendingRun?.startedAt ?? 0) > RUN_TIMEOUT_MS;
    if (hasNew || timedOut) endRun(soulId);
  }, [isPending, reports, pendingRun, soulId, endRun]);

  if (status === 'pending') {
    return (
      <div className="space-y-3">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  return (
    <div className="relative">
      {/* the thought stream's spine */}
      <div className="absolute top-3 bottom-3 left-[9px] w-px bg-zinc-200" />

      <div className="space-y-3">
        {isPending && <ThinkingThought />}

        {!reports?.length && !isPending ? (
          <div className="relative pl-7">
            <Node />
            <div className="rounded-xl border border-dashed border-zinc-200 py-8 text-center text-sm text-zinc-500">
              No thoughts recorded yet.
            </div>
          </div>
        ) : (
          reports?.map((report, idx) => (
            <div
              key={report.id}
              className="agent-thought-in relative pl-7"
              style={{ animationDelay: `${idx * 70}ms` }}
            >
              <Node pulse={report.isNew} />
              <EventReportCard
                report={report}
                onOpen={() => onOpenReport(report.id)}
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
}
